import React from 'react';
import styled from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { Icon } from '../Icon';
import { ModalHeader, ModalTitle } from '../Modal';
import { HeaderCell } from '../Table';
import { H3, Text } from '../Typography';

// Modal
export const StyledModalHeader = styled(ModalHeader)`
  padding-bottom: ${toRem(theme.space['3xl'])};
`;

export const HeaderContent = styled.div`
  flex-flow: column nowrap;
  justify-items: center;
  align-items: center;
  width: 100%;

  > * {
    margin-bottom: 0;
  }
`;

export const Name = styled(ModalTitle)``;

export const Entity = styled(H3)`
  margin-top: ${toRem(theme.space.sm)};
  font-weight: ${theme.font.weight.regular};
`;

export const Instructions = styled(Text).attrs({
  size: 'xsmall',
})`
  display: flex;
  align-self: center;
  width: 100%;
  max-width: ${toRem(460)};
  margin-bottom: ${toRem(theme.space['3xl'])};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.xl)};
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[100]};
  text-align: center;
  color: ${theme.color.neutral[800]};
`;

// Table
export const TableContainer = styled.div`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  /* off-scale exception: 1px keeps the last row's border clear of the overflow clip (2026-09-16) */
  padding-bottom: ${toRem(1)};
  overflow-x: auto;
  overflow-y: hidden;
`;

export const ReviewTable = styled.table`
  width: 100%;
  height: auto;
  border-collapse: collapse;
`;

export const TableBody = styled.tbody``;

export const Row = styled.tr`
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  text-align: left;
  color: ${theme.color.neutral[700]};

  @media (min-width: ${theme.breakpoint.small}px) {
    font-size: ${toRem(theme.font.size.sm)};
    line-height: ${lineHeight(theme.font.size.sm)};
  }
`;

const Cell = styled.td`
  height: ${toRem(theme.table.cell.height)};
  padding-right: ${toRem(theme.space.xl)};

  strong {
    font-weight: ${theme.font.weight.medium};
    color: ${theme.color.neutral[900]};
  }

  &:first-child {
    display: none;
  }

  &:nth-child(2) {
    padding-left: ${toRem(theme.space.xl)};
  }

  &:last-child {
    width: ${toRem(16.5)};
    padding-right: 0;
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    padding-right: ${toRem(theme.space['2xl'])};

    &:first-child {
      display: table-cell;
    }

    &:nth-child(2) {
      padding-left: 0;
    }
  }
`;

export const StyledHeaderCell = styled(HeaderCell)`
  padding: 0 ${toRem(theme.space.xl)} 0 0;

  &:first-child {
    display: none;
  }

  &:nth-child(2) {
    padding-left: ${toRem(theme.space.xl)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 ${toRem(theme.space['2xl'])} 0 0;

    &:first-child {
      display: table-cell;
    }

    &:nth-child(2) {
      padding-left: 0;
    }
  }
`;

export const TableHead = styled.thead`
  ${StyledHeaderCell} {
    padding-bottom: ${toRem(theme.space.md)};
  }
`;

interface ReviewRowProps {
  label: string;
  expected?: string;
  received?: string;
  hideIcon?: boolean;
}

export const ReviewRow: React.FC<ReviewRowProps> = ({ label, expected, received, hideIcon }) => {
  const noMatch = expected !== received;

  return (
    <Row>
      <StyledHeaderCell as="td">{label}</StyledHeaderCell>
      <Cell>{expected}</Cell>
      <Cell>
        <strong>{received}</strong>
      </Cell>
      <Cell>{hideIcon ? null : noMatch ? <Icon icon="RemoveThick" width={16.5} height={16.5} fill={theme.color.danger[50]} /> : <Icon icon="CheckmarkThick" width={16.5} height={16.5} fill={theme.color.success[50]} />}</Cell>
    </Row>
  );
};
